// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Field } from 'redux-form';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
// Components
import PublishAutocompleteLocationInput from '../../../containers/PublishAutocompleteLocationInput/PublishAutocompleteLocationInput';
import OMTooltip from '../../OMComponents/OMTooltip/OMTooltip';
import PublishFormCountriesSelect from './PublishFormCountriesSelect';
// Helpers
import { enterKey, publishFormLocationErrors, publishFormErrorNames, language, ISRAEL_COUNTRY_CODE } from '../../../constants';
import { trackPage, trackEvent } from '../../../utils/analytics/analyticsManager';
import { eventCategory, eventAction, eventLabel } from '../../../utils/analytics/analyticsConstants';
import { maxThreeDigit } from '../../../helpers/reduxFormNormalizers';
import GoogleService from '../../../helpers/googleService';
import slugify from '../../../helpers/slugify';
import TranslationService from '../../../services/TranslationService';
import CountriesService from '../../../services/CountriesService';
import MapboxService from '../../../services/MapboxService/MapboxService';
// Styles
import * as styles from './PublishFormLocationInput.scss';


const placeholderMap = {
  country_name: 'country',
  city_name: 'city',
  street_name: 'street'
};

class PublishFormLocationInput extends Component {
  constructor(props) {
    super(props);
    this.trackSearch = debounce(this.trackSearch.bind(this), 1000);
    this.debouncedGetAutocompleteSuggestions = debounce(this.getAutocompleteSuggestions, 500, { leading: false });

    this.state = {
      suggestions: [],
      fieldInFocus: '',
      errors: [],
      showTooltip: false,
      isoCode: ''
    };
  }

  componentDidMount() {
    CountriesService.load().then((countries) => {
      if (this.props.address.country_name || this.props.isIsraelOnly) {
        this.setState({
          isoCode:
            this.getCountryCode(this.props.address.country_name) ||
            GoogleService.OPTIONS.componentRestrictions.country
        });
      } else {
        const israel = countries.find(({ isoCode }) => isoCode === ISRAEL_COUNTRY_CODE);
        if (!israel) return;
        this.handleChangeCountry(israel, false);
      }
    });
    this.country = this.country || document.getElementsByName(`${this.props.name}.country_name`)[0];
    this.city = this.city || document.getElementsByName(`${this.props.name}.city_name`)[0];
    this.street = this.street || document.getElementsByName(`${this.props.name}.street_name`)[0];

    this.props.getComponentInstance && this.props.getComponentInstance(this);
  }

  componentWillReceiveProps(newProps) {
    if (!get(this.props, 'address.house_number') && get(newProps, 'address.house_number')) {
      this.setState({ errors: [] });
    }
  }

  onHouseNumberBlur = (event) => {
    if (!event.target.value && !this.props.isForeign) {
      this.setError(publishFormErrorNames.houseIsRequired);
      this.props.clearAddressPart('house_number', '', true);
      return;
    }

    const house_number = event.target.value;

    if (!this.props.manualAddress) {
      this.tryToFindFullAddress(house_number);
    } else {
      this.props.setHomeNumberManually(house_number);
    }
  };

  onHouseKeyDown = (event) => {
    if (event.target.value) {
      this.clearErrors(error => !error.startsWith('HOUSE'));
    }
    if (event.keyCode === enterKey) {
      event.preventDefault();
      document.activeElement.blur();
    }
  };

  onQueryStringChange = (type, query) => {
    const { address } = this.props;
    if (type === 'city_name' && address.street_name) {
      this.clearErrors(error => !error.startsWith('HOUSE') && !error.startsWith('ADDRESS'));
    } else if (type === 'street_name' && address.house_number) {
      this.clearErrors(error => !error.startsWith('HOUSE') && !error.startsWith('ADDRESS'));
    }

    if (type === 'country_name' && (address.country_name && address.country_name !== query)) {
      this.props.clearAddressPart(type, query);
    }

    if (type === 'city_name' && (address.city_name && address.city_name !== query)) {
      this.props.clearAddressPart(type, query);
    }

    if (type === 'street_name' && (address.street_name && address.street_name !== query)) {
      this.props.clearAddressPart(type, query);
    }

    if (!query) {
      type === 'house_number' && this.props.clearAddressPart(type);
    } else {
      this.clearErrors(error => !error.startsWith('HOUSE') && !error.startsWith('ADDRESS'));
    }
  };

  getAutocompleteRequestType = type => {
    if (type === 'country_name') return ['(regions)'];
    if (type === 'city_name') return ['(cities)'];
    return ['geocode'];
  }

  getAutocompleteQuery = (query, type) => {
    const typeMapping = {
      country_name: query,
      city_name: this.props.isForeign ? `${this.props.address.country_name} ${query}` : `${query}`,
      street_name: `${this.props.address.city_name} ${query}`,
      house_number: `${this.props.address.city_name} ${this.props.address.street_name} ${query}`
    };
    return typeMapping[type];
  };

  getSuggestionsOnLocus = (newValue) => {
    const isForeign = this.props.isForeign;
    if (isForeign) {
      const queryString = this.getAutocompleteQuery(newValue.query, newValue.type);
      const isoCode = this.getCountryCode(this.props.address.country_name);

      const locusSearchConfig = {
        country: isoCode,
        overseasOnly: true,
        locale: this.props.locale
      };
      const queryTypes = this.getAutocompleteRequestType(newValue.type);
      return this.props.googleService.autocompleteLocusViaGoogle(queryString, queryTypes[0], locusSearchConfig);
    }
    const [cityName] = get(this.props, 'fullAddressModel.tags', []);
    const parentTags = cityName ? [cityName] : undefined;
    const type = parentTags ? 'street' : 'city';
    return this.props.googleService.autocompleteLocusViaInternalBase(
      newValue.query,
      parentTags,
      type,
      { locale: this.props.locale }
    );
  };

  getAutocompleteSuggestions = (newValue) => {
    if (!this.props.googleService) { return null; }
    this.setState({ errors: [] });

    return this.getSuggestionsOnLocus(newValue)
      .then(suggestions => {
        const isStreet = get(newValue, 'type') === 'street_name';
        const { isForeign } = this.props;
        if (!suggestions.length && (!isStreet || isForeign)) {
          return this.tryAutocompleteGoogle(newValue);
        }
        this.setState({ suggestions });
        return suggestions;
      })
      .catch(() => this.tryAutocompleteGoogle(newValue));
  }

  getErrorTooltip = ([firstError, ...rest]) => {
    const errorMap = {
      HOUSE_NOT_EXISTS: this.props.translate('canNotFindAddress'),
      HOUSE_IS_REQUIRED: this.props.translate('enterHouseNumber'),
      ADDRESS_IS_INCOMPLETE: this.props.translate('fullAddressMessage'),
      ADDRESS_IS_CITY: this.props.translate('cityMessage'),
      ADDRESS_WAS_NOT_CREATED: this.props.translate('addressWasNotCreated'),
      MESSAGE_ERROR_ADDRESS: this.props.rootTranslate('messageErrorAddress')
    };
    return errorMap[firstError] || '';
  };

  getCountryCode = (country) => CountriesService.getCountryCode(country);

  setError(error, callback) {
    this.setState(prevState => ({
      errors: uniq([...prevState.errors, error])
    }), callback || noop);
  }

  clearErrors(predicate, callback) {
    this.setState(prevState => ({
      errors: prevState.errors.filter(predicate)
    }), callback || noop);
  }

  handleFocus = (type) => {
    this.props.setManualButtonState(false);
    this.setState({ fieldInFocus: type });
    this.country = this.country || document.getElementsByName(`${this.props.name}.country_name`)[0];
    this.city = this.city || document.getElementsByName(`${this.props.name}.city_name`)[0];
    this.street = this.street || document.getElementsByName(`${this.props.name}.street_name`)[0];
    this.clearErrors(error => !error.startsWith('HOUSE') && !error.startsWith('ADDRESS'));

    const targetName = document.activeElement.name;

    if (this.country && targetName === this.country.name) {
      // nothing to do here
    } else if (!this.props.address.country_name && this.country) {
      if (!this.state.loading || targetName !== this.city.name) {
        this.country.focus();
      }
    } else if (targetName === this.city.name && this.props.address.country_name && !this.state.loading) {
      // nothing to do here
    } else if (!this.props.address.city_name) {
      if (!this.state.loading || targetName !== this.street.name) {
        this.city.focus();
      }
    } else if (this.props.address.city_name && !this.props.address.street_name && !this.state.loading) {
      this.street.focus();
    }
  };

  isInvalidField = (type) => {
    const { isForeign, address } = this.props;
    const { errors } = this.state;
    const hasError = errors.some(error => publishFormLocationErrors.has(error));

    if (isForeign) {
      return type !== 'street_name' && type !== 'house_number' && !address[type] && hasError;
    }

    return !address[type] && hasError;
  }

  handleBlur = (query) => {
    const newState = {};
    if (query && this.state.suggestions.length) {
      newState.loading = true;
      this.searchSelectedOption(this.state.suggestions[0]);
    }

    newState.suggestion = [];

    this.setState({ ...newState });

    if (this.props.isForeign) {
      if (!get(this.props, 'address.city_name')) {
        this.setError(publishFormErrorNames.addressIsCity);
      }
    } else if (!query || !this.props.isFullAddress) {
      this.setError(publishFormErrorNames.addressIsIncomplete);
    }
  };

  trackSearch = (value) => {
    trackPage(`/search?cat=publish&q=${value}`);
  };

  validateAddress = (address, method) => {
    if (address && !address.isFull && method !== 'click') {
      trackEvent(eventCategory.error, eventAction.search, eventLabel.notValidAddress);
    }
  };

  tryAutocompleteGoogle = (newValue) => {
    const queryString = this.getAutocompleteQuery(newValue.query, newValue.type);
    const queryType = this.getAutocompleteRequestType(newValue.type);

    const isoCode = this.getCountryCode(
      get(this.props, 'address.country_name', 'Israel')
    );

    const promise = this.props.googleService.autocompleteGoogle(
      queryString,
      queryType,
      {
        componentRestrictions: { country: isoCode }
      }
    );

    if (promise instanceof Promise) {
      return promise
        .then(suggestions => {
          suggestions = suggestions.map(item => ({
            ...item,
            suggestionType: newValue.type
          }));
          this.trackSearch(newValue);
          this.setState({ suggestions });
          return suggestions;
        })
        .catch((err) => {
          this.props.googleService.handleError(err);
          this.setError(publishFormErrorNames.messageErrorAddress);
        });
    }
    return Promise.resolve();
  }

  tryToFindFullAddress = (house_number) => {
    this.props.setPreloaderState(true);
    const tags = get(this.props, 'fullAddressModel.tags');
    const getSuggestions = tags && !this.props.isForeign
      ? this.geocodeRequest({ tags: [...tags.slice(0, 2), house_number], inputValue: house_number })
      : this.getAutocompleteSuggestions({ query: `${house_number} `, type: 'house_number' })
        .then((suggestions = []) => (suggestions && suggestions.length)
          ? this.searchSelectedOption(
              { ...(suggestions[0]), inputValue: house_number }
            )
          : Promise.resolve()
        )
        .then(address => {
          if (!address) {
            // this.props.clearAddressPart('house_number');
            this.props.changeValue(this.props.name, {
              ...this.props.fullAddressModel,
              isFull: false,
              en: {
                ...this.props.fullAddressModel.en,
                house_number
              },
              he: {
                ...this.props.fullAddressModel.he,
                house_number
              }
            });
          }

          return Promise.resolve(address);
        });

    getSuggestions
      .then(data => {
        if (data && data.isFull) {
          this.props.getInputData(data);
        } else if (
          !this.props.isForeign
          || (!(get(data, 'en.country_name', false) || !get(data, 'en.city_name', false)))
        ) {
          this.props.setManualButtonState(true);
          this.setError(publishFormErrorNames.houseDoesntExist);
        }
      })
      .then(() => this.props.setPreloaderState(false));
  };

  searchFullAddressInLocus = (address, house) => {
    const addressData = address || this.props.fullAddressModel;
    const tags = addressData.tags
      ? [...addressData.tags.slice(0, 2), house]
      : [
          slugify(get(addressData, 'en.city_name', '')),
          slugify(get(addressData, 'en.street_name', '')),
          house
        ];

    return this.props.googleService.performGeocodeLocusByInnerBase(tags);
  };

  checkEnStreet = (enStreetName, data) => TranslationService.suggestStringLanguage(enStreetName)
    .then((res) => (res.language === 'he' && res.probability > 0.5)
      ? this.findAndGeocodeInLocus(enStreetName)
      : Promise.resolve(data)
    );

  findAndGeocodeInLocus = (streetName) => this.getSuggestionsOnLocus(streetName)
    .then(data => {
      if (data && data[0]) {
        return this.props.googleService.performGeocodeLocusByInnerBase(data[0].tags);
      }

      return Promise.reject(new Error('Not found'));
    });

  geocodeRequest = (selectedSuggestion, method) => {
    let address = (selectedSuggestion.description || '');
    address = address.replace(/"/g, '״');

    const geocode = selectedSuggestion.tags && !this.props.isForeign
      ? this.props.googleService.performGeocodeLocusByInnerBase(selectedSuggestion.tags)
      : this.props.googleService.geocodeLocusViaGoogle(
        selectedSuggestion.place_id,
        this.props.address.country_name
      );

    if (this.props.isForeign) {
      geocode.then((data) => {
        const enCityName = get(data, 'en.city_name');
        return (enCityName && !selectedSuggestion.tags) ? this.checkEnStreet(enCityName, data) : data;
      });
    }
    geocode
      .then((data) => {
        const enStreetName = get(data, 'en.street_name');
        return (enStreetName && !selectedSuggestion.tags) ? this.checkEnStreet(enStreetName, data) : data;
      })
      .then((resp = {}) => {
        let response = selectedSuggestion.suggestionType === 'street_name' && resp.isFull
          ? {
            ...resp,
            isFull: false,
            en: {
              ...resp.en,
              house_number: null
            },
            he: {
              ...resp.he,
              house_number: null
            }
          }
          : resp;

        if (!response) {
          response = {
          ...this.props.fullAddressModel,
            isFull: false,
            en: {
              ...this.props.fullAddressModel.en,
              house_number: selectedSuggestion.inputValue
            },
            he: {
              ...this.props.fullAddressModel.he,
              house_number: selectedSuggestion.inputValue
            }
          };
        }

        this.validateAddress(response, method);
        this.props.getInputData(response);
        this.props.setHomeNumberManually(selectedSuggestion.inputValue);

        this.setState({ loading: false });
        return response;
      })
      .catch((err) => {
        this.props.googleService.handleError(err);
        this.setState({ loading: false });
        return err;
      });

    return geocode;
  };

  // Is called when user has selected a suggestion
  searchSelectedOption = (selectedSuggestion, method) => {
    this.setState({ suggestions: [] });
    return this.geocodeRequest(selectedSuggestion, method);
  };

  createInputParameters = name => ({
    name: `${this.props.name}.${name}`,
    suggestions: this.state.suggestions,
    initialValue: this.props.address[name] || this.props[name] || '',
    searchSelectedOption: this.searchSelectedOption,
    className: cx(styles[name]),
    locale: this.props.locale,
    receiveSuggestions: this.debouncedGetAutocompleteSuggestions,
    placeholder: this.props.translate(placeholderMap[name]).toString(),
    fieldValue: this.props.address ? this.props.address[name] : {},
    onQueryStringChange: this.onQueryStringChange,
    handleFocus: this.handleFocus,
    inFocus: this.state.fieldInFocus === name,
    handleBlur: this.handleBlur,
    params: {
      type: name
    }
  });

  handleChangeCountry = (value, focus = true) => {
    if (!value) {
      this.props.clearAddressPart('country_name', value);
      return;
    }
    const allCountries = CountriesService.get();
    if (!allCountries) return;

    const currentCountry = allCountries.find((el) => el.id === value.id);
    this.setState({ isoCode: currentCountry.isoCode });
    const data = {
      ...this.props.fullAddressModel,
      zoom: currentCountry?.isoCode === ISRAEL_COUNTRY_CODE
        ? MapboxService.getMapOptions('foreignPublish')?.zoom
        : 5,
      tags: undefined,
      polygon: undefined,
      isFull: false,
      google_place_id: '',
      location: {
        lat: currentCountry.location[0],
        lon: currentCountry.location[1]
      },
      map: currentCountry.bounds,
      en: {
        country_name: currentCountry.address.en
      },
      he: {
        country_name: currentCountry.address.he
      }
    };
    this.props.getInputData(data);

    if (focus) {
      this.city.focus();
    }
  };

  renderInfoTooltipButton = () => this.props.withTooltipImage && (
    <img
      className={styles.infoTooltipButton}
      src={!!this.state.errors.length ? '/icons/exclamation-button.png' : '/icons/exclamation-mark.png'}
      alt="warning-icon"
      tabIndex={0}
      onBlur={() => this.setState({ showTooltip: false })}
      onClick={() => this.setState({ showTooltip: !this.state.showTooltip })}
    />
  );

  render() {
    const houseNumber = get(this.props, 'address.house_number');
    return (
      <div>
        <div className={styles.fullAddressTitle}>{this.props.translate('enterFullAddress')}</div>
        <div className={cx(styles.locationInputWrapper, { [styles.error]: !!this.state.errors.length })}>
          <div
            className={cx(
              styles.autocompleteWrapper,
              { [styles.foreignFormWrapper]: !this.props.isIsraelOnly }
            )}
          >
            {!this.props.isIsraelOnly && (
              <div className={cx(
                styles.inputWrapper,
                styles.country,
                { [styles.invalidAddress]: this.isInvalidField('country_name') })}
              >
                <PublishFormCountriesSelect
                  address={this.props.address}
                  country_name={this.props.country_name}
                  onChangeCountry={this.handleChangeCountry}
                  isRtl={this.props.isRtl}
                  locale={this.props.locale}
                  translate={this.props.translate}
                />
              </div>
            )}
            <div className={cx(
              styles.inputWrapper,
              styles.city,
              { [styles.invalidAddress]: this.isInvalidField('city_name') })}
            >
              <PublishAutocompleteLocationInput
                {...this.createInputParameters('city_name')}
              />
            </div>
            <div className={cx(
              styles.inputWrapper,
              styles.street,
              { [styles.invalidAddress]: this.isInvalidField('street_name') })}
            >
              <PublishAutocompleteLocationInput
                {...this.createInputParameters('street_name')}
              />
            </div>
            <div className={cx(
              styles.inputWrapper,
              styles.house,
              { [styles.withTooltipImage]: this.props.withTooltipImage },
              { [styles.withTooltipImage]: this.props.withTooltipImage },
              { [styles.invalidAddress]: this.isInvalidField('house_number') })}
            >
              <Field
                type="text"
                name={`${this.props.name}.${this.props.isDefaultLanguage
                  ? language.fallback
                  : this.props.locale}.house_number`
                }
                value={houseNumber}
                component="input"
                placeholder={this.props.translate('house')}
                normalize={!this.props.isForeign && maxThreeDigit}
                onFocus={this.handleFocus}
                onBlur={this.onHouseNumberBlur}
                onKeyDown={this.onHouseKeyDown}
                onChange={(e, value) => this.onQueryStringChange('house_number', value)}
              />

              {this.renderInfoTooltipButton()}

              {(this.props.isRealtor && this.state.showTooltip) && (
                <OMTooltip
                  msg={`${this.props.translate('houseNumberIsHidden')}`}
                  className="mobileAuthenticationTooltip"
                />
              )}
              {houseNumber && !this.props.isFullAddress && !this.props.manualAddress && (
                <button onClick={evt => evt.preventDefault()} className={styles.locationInputEnterButton} />
              )}
            </div>
          </div>
          {!!this.state.errors.length && (
            <div className={styles.errorMessage}>
              {this.getErrorTooltip(this.state.errors)}
            </div>
          )}
        </div>
      </div>
    );
  }
}

PublishFormLocationInput.propTypes = {
  placeholder: PropTypes.string,
  locale: PropTypes.string,
  token: PropTypes.string,
  getInputData: PropTypes.func,
  googleService: PropTypes.object,
  autocompleteGoogle: PropTypes.func,
  initialValue: PropTypes.object,
  isInvalid: PropTypes.bool,
  isMobile: PropTypes.bool,
  manualAddress: PropTypes.bool,
  isFullAddress: PropTypes.bool,
  translate: PropTypes.func,
  changeValue: PropTypes.func,
  getComponentInstance: PropTypes.func,
  setHomeNumberManually: PropTypes.func,
  setPreloaderState: PropTypes.func,
  setManualButtonState: PropTypes.func,
  clearAddressPart: PropTypes.func,
  address: PropTypes.object,
  fullAddressModel: PropTypes.object,
  isDefaultLanguage: PropTypes.bool,
  withTooltipImage: PropTypes.bool,
  isRealtor: PropTypes.bool,
  city_name: PropTypes.string,
  street_name: PropTypes.string,
  country_name: PropTypes.string,
  isForeign: PropTypes.bool,
  isRtl: PropTypes.bool,
  isIsraelOnly: PropTypes.bool,
  name: PropTypes.string,
  rootTranslate: PropTypes.func
};

export default PublishFormLocationInput;
